// Parses a CSV/TSV document into a rectangular table for `CsvDocView`. Hand-rolled rather than a
// dependency: RFC 4180 is small, the rules that matter are the quoting ones, and a parser is exactly the
// kind of thing this package can unit-test (jest runs `testEnvironment: 'node'`, so a pure module is the
// only thing that CAN be tested here).

/** The parsed sheet. `rows` never includes the header row, and every row is padded to `columnCount` so the
 *  rendered `<table>` is always rectangular. */
export interface CsvTable {
  /** The first record, when it reads like a header (see `parseCsv`). `null` when it does not. */
  header: string[] | null;
  rows: string[][];
  columnCount: number;
  /** True when the document had more rows than `MAX_CSV_ROWS` and the tail was dropped. */
  truncated: boolean;
}

/** The row cap, and the ONE place in the viewer that truncates instead of refusing.
 *
 *  Every other over-size path (`readBoundedBytes`, `inlineDocTooLarge`) refuses, because a document cut
 *  mid-tag or mid-sentence *lies* — it silently says something its author didn't. A table is the exception
 *  that earns different treatment: rows are independent records, so dropping the tail removes information
 *  without falsifying what remains, and the view says so (`docViewer.csvTruncated`) with Download beside it.
 *
 *  The cap is needed even though bytes are already bounded: 2 MB of CSV is ~40k rows, and a non-virtualised
 *  40k-row `<table>` is a hard freeze — the same hazard `MAX_DOC_PREVIEW_BYTES` documents for markdown. */
export const MAX_CSV_ROWS = 2000;

export type CsvDelimiter = ',' | ';' | '\t';

const DELIMITERS: readonly CsvDelimiter[] = [',', ';', '\t'];

/** Which delimiter a document uses, decided from its FIRST record: whichever candidate occurs most often
 *  OUTSIDE quotes wins, `,` on a tie.
 *
 *  Semicolon support is not exotica — Excel exports `;` in every locale whose decimal separator is a comma,
 *  so a European user's export is the common case, not the edge one. Counting outside quotes matters for the
 *  same reason: `"Berlin, Germany";42` has more commas than semicolons but is semicolon-delimited. */
export function detectDelimiter(firstRecord: string): CsvDelimiter {
  const counts = new Map<CsvDelimiter, number>(DELIMITERS.map((d) => [d, 0]));
  let quoted = false;
  for (let i = 0; i < firstRecord.length; i += 1) {
    const char = firstRecord[i];
    if (char === '"') {
      // A doubled quote inside a quoted field is an escaped literal, not a close.
      if (quoted && firstRecord[i + 1] === '"') {
        i += 1;
        continue;
      }
      quoted = !quoted;
      continue;
    }
    if (quoted) continue;
    const candidate = DELIMITERS.find((d) => d === char);
    if (candidate) counts.set(candidate, (counts.get(candidate) ?? 0) + 1);
  }
  let best: CsvDelimiter = ',';
  for (const delimiter of DELIMITERS) {
    if ((counts.get(delimiter) ?? 0) > (counts.get(best) ?? 0)) best = delimiter;
  }
  return best;
}

/** Splits `text` into records of fields, RFC 4180.
 *
 *  Rules that are behaviour rather than detail:
 *   - records end on CRLF, LF **or** a lone CR (old Mac exports, and a stray CR must not become a cell);
 *   - a field may be `"`-quoted, and inside quotes `""` is a literal `"` while the delimiter, CR and LF are
 *     ordinary characters;
 *   - leading and trailing spaces are **preserved** — RFC 4180 makes them part of the field, and trimming
 *     would silently edit someone's data;
 *   - a trailing newline does not produce an empty final record.
 */
function splitRecords(text: string, delimiter: CsvDelimiter): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let quoted = false;

  const endField = () => {
    record.push(field);
    field = '';
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
        continue;
      }
      field += char;
      continue;
    }

    if (char === '"') {
      quoted = true;
      continue;
    }
    if (char === delimiter) {
      endField();
      continue;
    }
    if (char === '\r') {
      // CRLF is one terminator, not two.
      if (text[i + 1] === '\n') i += 1;
      endRecord();
      continue;
    }
    if (char === '\n') {
      endRecord();
      continue;
    }
    field += char;
  }

  // A final record with no terminator still counts; a trailing terminator does not add an empty one.
  if (field !== '' || record.length > 0) endRecord();
  return records;
}

/** True when the first record reads like a header: every field non-empty, AND there is more than one record.
 *
 *  The second half is what stops a one-column list of values (`Alice\nBob\nCarol`) from having its first
 *  value eaten as a column title — with a single record there is nothing for a header to be a header OF. */
function looksLikeHeader(records: string[][]): boolean {
  if (records.length < 2) return false;
  return records[0].length > 0 && records[0].every((field) => field.trim() !== '');
}

export function parseCsv(text: string, opts: { maxRows?: number } = {}): CsvTable {
  const maxRows = opts.maxRows ?? MAX_CSV_ROWS;
  // A UTF-8 BOM belongs to the encoding, not to the first column's name — left in place it makes the first
  // header cell fail every string comparison while looking identical on screen.
  const body = text.replace(/^﻿/, '');
  if (body.trim() === '') {
    return { header: null, rows: [], columnCount: 0, truncated: false };
  }

  const firstBreak = body.search(/\r\n|\r|\n/);
  const firstRecord = firstBreak >= 0 ? body.slice(0, firstBreak) : body;
  const records = splitRecords(body, detectDelimiter(firstRecord));

  const header = looksLikeHeader(records) ? records[0] : null;
  const dataRecords = header ? records.slice(1) : records;

  const truncated = dataRecords.length > maxRows;
  const kept = truncated ? dataRecords.slice(0, maxRows) : dataRecords;

  // Ragged rows are padded rather than rejected: a real export can have a short last line, and a table with
  // uneven `<tr>` lengths renders as a broken grid.
  const columnCount = Math.max(header?.length ?? 0, ...kept.map((row) => row.length), 0);
  const rows = kept.map((row) => (row.length === columnCount ? row : [...row, ...Array(columnCount - row.length).fill('')]));

  return {
    header: header && header.length < columnCount ? [...header, ...Array(columnCount - header.length).fill('')] : header,
    rows,
    columnCount,
    truncated,
  };
}
